import React, { SelectHTMLAttributes, useState, useRef, useEffect } from 'react';

import './styles.css';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    name: string;
    label: string;
    options: Array<{
        id: string;
        label: string;
    }>;
}

const Select: React.FC<SelectProps> = ({ name, label, options, value, onChange, ...rest }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || '');
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedLabel = options.find(opt => opt.id === selectedValue)?.label || 'Selecione';

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (id: string) => {
        setSelectedValue(id);
        setIsOpen(false);

        // Trigger onChange like a normal select
        if (onChange) {
            const event = {
                target: { name, value: id }
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange(event);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedValue('');
        setIsOpen(false);

        // Trigger onChange with empty value
        if (onChange) {
            const event = {
                target: { name, value: '' }
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange(event);
        }
    };

    return (
        <div className="select-block" ref={containerRef}>
            <label htmlFor={name}>{label}</label>
            <div className="select-container">
                <button
                    type="button"
                    id={name}
                    className={`select-trigger ${isOpen ? 'open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    <span className="select-value">{selectedLabel}</span>
                    <div className="select-controls">
                        {selectedValue && (
                            <div
                                className="select-clear"
                                onClick={handleClear}
                                role="button"
                                tabIndex={0}
                                aria-label="Limpar seleção"
                            >
                                ✕
                            </div>
                        )}
                        <span className="select-arrow">▼</span>
                    </div>
                </button>

                {isOpen && (
                    <div className="select-menu" role="listbox">
                        {options.map(option => (
                            <div
                                key={option.id}
                                className={`select-option ${selectedValue === option.id ? 'selected' : ''}`}
                                onClick={() => handleSelect(option.id)}
                                role="option"
                                aria-selected={selectedValue === option.id}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <input type="hidden" name={name} value={selectedValue} />
        </div>
    )
}

export default Select